import React, { useCallback, useState, useEffect, useMemo, useRef } from 'react';
import './ItineraryManagement.css';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/Layout';
import StickyActionHeader from '../../components/StickyActionHeader';
import WhatsAppSendConfirmationDialog from '../../components/WhatsAppSendConfirmationDialog';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { ToggleGroup, ToggleGroupItem } from '../../components/ui/toggle-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { 
  Plus, 
  CalendarCheck,
  MapPinLine,
  Clock,
  TShirt,
  PencilSimple,
  DotsThree,
  Trash,
  WhatsappLogo,
} from '@phosphor-icons/react';
import Bed from '../../components/icons/RoomBedIcon';
import api from '../../services/api';
import { toast } from 'sonner';
import { format } from 'date-fns';
import EventAwareDatePicker from '../../components/EventAwareDatePicker';
import VenueSelector from '../../components/VenueSelector';
import {
  getBlockingItineraryDeleteStayPlan,
  getVenueKey,
  findVenueHotel,
} from './itineraryStayUtils';
import { getVenueLocationUrl, hasVenueCoordinates } from '../../lib/locationLinks';
import useEventTypeConfig from '../../hooks/useEventTypeConfig';
import { GuestItineraryRsvpProvider } from '../guest/GuestItineraryRsvpControls';

const createEmptyItineraryItem = () => ({
  name: '',
  description: '',
  date: '',
  start_time: '',
  end_time: '',
  dress_code: '',
  side: 'both',
  needs_stay: false,
  venue: {
    name: '',
    address: '',
    hall_name: '',
    floor: '',
    notes: '',
  },
});

export default function ItineraryManagement() {
  const itineraryPageRef = useRef(null);
  const { currentEvent, effectiveRole, hasAdminAccess, loadUser, user } = useAuth();
  const eventTypeConfig = useEventTypeConfig(currentEvent);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const focusItemId = searchParams.get('focus');
  useEffect(() => {
    if (!focusItemId) return undefined;
    let clearTimer = null;
    let attempt = 0;
    const poll = window.setInterval(() => {
      attempt += 1;
      const node = document.querySelector(`[data-testid="itinerary-${CSS.escape(focusItemId)}"]`);
      if (node) {
        window.clearInterval(poll);
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        node.classList.add('ring-2', 'ring-wedding-sage', 'ring-offset-2', 'animate-pulse');
        clearTimer = window.setTimeout(() => node.classList.remove('animate-pulse'), 2400);
      } else if (attempt >= 20) {
        window.clearInterval(poll);
      }
    }, 300);
    return () => {
      window.clearInterval(poll);
      if (clearTimer) window.clearTimeout(clearTimer);
    };
  }, [focusItemId]);
  const isGuestItinerary = location.pathname.startsWith('/guest/');
  const isGuestInvitationView = isGuestItinerary
    && ['guest', 'admin_guest'].includes(effectiveRole || user?.role);
  const canManageItinerary = hasAdminAccess && !isGuestItinerary;
  const [guestInvitationScope, setGuestInvitationScope] = useState({ eventId: null, ids: [] });
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [sendingItineraryMessage, setSendingItineraryMessage] = useState(false);
  const [confirmItineraryMessage, setConfirmItineraryMessage] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deletingItem, setDeletingItem] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [loadingStayData, setLoadingStayData] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingOriginalItem, setEditingOriginalItem] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [stayPlans, setStayPlans] = useState([]);
  const [addingItem, setAddingItem] = useState(false);
  const [newItem, setNewItem] = useState(createEmptyItineraryItem);

  const itinerary = currentEvent?.itinerary || [];
  const guestInvitationScopeLoaded = guestInvitationScope.eventId === currentEvent?.id;
  const visibleItinerary = isGuestInvitationView
    ? itinerary.filter((item) => (
        guestInvitationScopeLoaded && guestInvitationScope.ids.includes(item.id)
      ))
    : itinerary;
  const handleInvitationScopeLoaded = useCallback((ids) => {
    setGuestInvitationScope({ eventId: currentEvent?.id || null, ids });
  }, [currentEvent?.id]);
  const savedVenues = useMemo(() => {
    const seen = new Set();
    return (currentEvent?.itinerary || [])
      .map((item) => item.venue)
      .filter((venue) => {
        const key = getVenueKey(venue);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [currentEvent?.itinerary]);
  const unmappedVenueCount = itinerary.filter(
    (item) => (item.venue?.name || item.venue?.address) && !hasVenueCoordinates(item.venue)
  ).length;
  const blockingDeleteStayPlan = useMemo(
    () => getBlockingItineraryDeleteStayPlan(itemToDelete, stayPlans),
    [itemToDelete, stayPlans]
  );
  const blockingAssignedGuestCount = Number(blockingDeleteStayPlan?.assigned_guest_count || 0);
  const blockingRoomsPath = blockingDeleteStayPlan?.id
    ? `/dashboard/rooms?tab=rooms&stay_plan=${encodeURIComponent(blockingDeleteStayPlan.id)}`
    : '/dashboard/rooms?tab=rooms';

  const loadStayData = useCallback(async () => {
    if (!eventTypeConfig.features.showAccommodation) {
      setHotels([]);
      setStayPlans([]);
      setLoadingStayData(false);
      return;
    }
    if (!currentEvent?.id || !hasAdminAccess) {
      setLoadingStayData(false);
      return;
    }
    setLoadingStayData(true);
    try {
      const [hotelsResponse, stayPlansResponse] = await Promise.all([
        api.get(`/events/${currentEvent.id}/hotels`),
        api.get(`/events/${currentEvent.id}/stay-plans`),
      ]);
      setHotels(hotelsResponse.data || []);
      setStayPlans(stayPlansResponse.data || []);
    } catch (error) {
      console.error('Failed to load hotel stay data:', error);
    } finally {
      setLoadingStayData(false);
    }
  }, [currentEvent?.id, eventTypeConfig.features.showAccommodation, hasAdminAccess]);

  useEffect(() => {
    loadStayData();
  }, [loadStayData]);

  const finishAddItemFlow = () => {
    setShowAddDialog(false);
    setNewItem(createEmptyItineraryItem());
  };

  const createItineraryItem = async () => {
    const response = await api.post(`/events/${currentEvent.id}/itinerary`, {
      ...newItem,
      side: eventTypeConfig.features.showGuestSides ? (newItem.side || 'both') : 'both',
      needs_stay: eventTypeConfig.features.showAccommodation && newItem.needs_stay === true,
      date: new Date(newItem.date).toISOString(),
    });
    const previousIds = new Set(itinerary.map((item) => item.id));
    const updatedItinerary = response.data?.itinerary || [];
    return updatedItinerary.find((item) => !previousIds.has(item.id))
      || updatedItinerary[updatedItinerary.length - 1];
  };

  const linkVenueHotelOnly = async (item) => {
    const venue = item?.venue || {};
    return api.post(
      `/events/${currentEvent.id}/itinerary/${item.id}/stay-link`,
      {
        action: 'link_hotel_only',
        hotel_name: venue.name || item.name,
        address: venue.address || null,
        venue_key: getVenueKey(venue) || null,
        venue_name: venue.name || null,
        venue_address: venue.address || null,
        lat: venue.lat ?? null,
        lng: venue.lng ?? null,
      }
    );
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    let createdItem = null;
    try {
      setAddingItem(true);
      createdItem = await createItineraryItem();
      if (newItem.needs_stay && createdItem?.id) {
        const hadVenueHotel = Boolean(findVenueHotel(hotels, createdItem.venue));
        await linkVenueHotelOnly(createdItem);
        toast.info(
          hadVenueHotel
            ? 'Hotel already exists for this venue, so stay dates were updated without adding a duplicate.'
            : 'Hotel and stay dates added from venue details. You can review them in Rooms.',
          { duration: 7000 }
        );
      }
      toast.success('Event added to itinerary');
      finishAddItemFlow();
      await Promise.all([loadUser(), loadStayData()]);
    } catch (error) {
      if (createdItem?.id) {
        try {
          await api.delete(`/events/${currentEvent.id}/itinerary/${createdItem.id}`);
          await Promise.all([loadUser(), loadStayData()]);
        } catch (rollbackError) {
          console.error('Failed to roll back itinerary event after hotel setup error:', rollbackError);
        }
      }
      toast.error(error.response?.data?.detail || 'Failed to add event');
    } finally {
      setAddingItem(false);
    }
  };

  const handleEditItem = (item) => {
    setEditingOriginalItem(item);
    setEditingItem({
      ...item,
      side: item.side || 'both',
      date: item.date ? item.date.split('T')[0] : '',
      venue: item.venue || { name: '', address: '', hall_name: '', floor: '', notes: '' }
    });
    setShowEditDialog(true);
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    try {
      const nextNeedsStay = eventTypeConfig.features.showAccommodation && editingItem.needs_stay === true;
      const removingStay = !nextNeedsStay && editingOriginalItem?.needs_stay;
      if (removingStay) {
        await api.post(
          `/events/${currentEvent.id}/itinerary/${editingItem.id}/stay-link`,
          { action: 'clear' }
        );
      }
      const itemPayload = {
        ...editingItem,
        side: eventTypeConfig.features.showGuestSides ? (editingItem.side || 'both') : 'both',
        needs_stay: nextNeedsStay,
        date: new Date(editingItem.date).toISOString(),
        hotel_id: removingStay ? null : editingItem.hotel_id,
        stay_plan_id: removingStay ? null : editingItem.stay_plan_id,
      };
      const response = await api.put(
        `/events/${currentEvent.id}/itinerary/${editingItem.id}`,
        itemPayload
      );
      const updatedItem = (response.data?.itinerary || []).find((item) => item.id === editingItem.id)
        || editingItem;
      if (updatedItem.needs_stay) {
        const hadVenueHotel = Boolean(findVenueHotel(hotels, updatedItem.venue));
        await linkVenueHotelOnly(updatedItem);
        toast.info(
          hadVenueHotel
            ? 'Hotel stay dates updated to cover this venue\'s events.'
            : 'Hotel and stay dates added from venue details. You can review them in Rooms.',
          { duration: 7000 }
        );
      }
      toast.success('Event updated');
      setShowEditDialog(false);
      setEditingItem(null);
      setEditingOriginalItem(null);
      await loadUser();
      await loadStayData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    }
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete?.id) return;
    setDeletingItem(true);
    setDeleteError('');
    try {
      await api.delete(`/events/${currentEvent.id}/itinerary/${itemToDelete.id}`);
      toast.success('Event deleted');
      setItemToDelete(null);
      await Promise.all([loadUser(), loadStayData()]);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string'
        ? detail
        : detail?.message || 'This itinerary event could not be deleted. Please try again.';
      toast.error(message, { duration: 9000 });
      if (error.response?.status === 409) {
        await loadStayData();
      } else {
        setDeleteError(message);
      }
    } finally {
      setDeletingItem(false);
    }
  };

  const sendItineraryUpdateMessage = async () => {
    if (!currentEvent?.id) return;

    const formData = new FormData();
    formData.append('template_type', 'itinerary_update');

    try {
      setSendingItineraryMessage(true);
      const readinessResponse = await api.get(`/events/${currentEvent.id}/whatsapp/readiness`);
      const recipientCount = Number(readinessResponse.data?.guests_with_valid_phone) || 0;
      toast.info(
        recipientCount === 1
          ? 'Sending WhatsApp message to 1 recipient...'
          : `Sending WhatsApp message to ${recipientCount} recipients...`,
      );
      const response = await api.post(
        `/events/${currentEvent.id}/whatsapp/send-template`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const summary = response.data?.summary || {};
      if (Number(summary.sent) > 0) {
        toast.success(`Itinerary update sent to ${summary.sent} guest(s)`);
      } else if (Number(summary.sending) > 0) {
        toast.success(`Itinerary update queued for ${summary.sending} guest(s)`);
      } else {
        toast.error('No itinerary updates were sent');
      }
      if (summary.failed || summary.skipped) {
        toast.info(`${summary.failed || 0} failed, ${summary.skipped || 0} skipped`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send itinerary update');
    } finally {
      setSendingItineraryMessage(false);
    }
  };

  // Group itinerary by date
  const groupedItinerary = visibleItinerary.reduce((acc, item) => {
    const date = format(new Date(item.date), 'yyyy-MM-dd');
    if (!acc[date]) acc[date] = [];
    acc[date].push(item);
    return acc;
  }, {});

  const getLinkedHotel = (item) => hotels.find((hotel) => hotel.id === item.hotel_id);

  const formatItineraryTime = (value) => {
    if (!value) return '';
    const [hours, minutes] = value.split(':').map(Number);
    const suffix = hours >= 12 ? 'PM' : 'AM';
    const hour = hours % 12 || 12;
    return `${hour}:${String(minutes || 0).padStart(2, '0')} ${suffix}`;
  };

  const getDuration = (start, end) => {
    if (!start || !end) return '';
    const [startHours, startMinutes] = start.split(':').map(Number);
    const [endHours, endMinutes] = end.split(':').map(Number);
    let minutes = (endHours * 60 + endMinutes) - (startHours * 60 + startMinutes);
    if (minutes < 0) minutes += 24 * 60;
    if (!minutes) return '';
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    if (!hours) return `${remainder} min`;
    return remainder ? `${hours} hr ${remainder} min` : `${hours} ${hours === 1 ? 'hr' : 'hrs'}`;
  };

  const getAudienceLabel = (side) => {
    if (side === 'bride') return 'Bride side';
    if (side === 'groom') return 'Groom side';
    return 'All guests';
  };

  const getEventStatus = (item) => {
    const eventDate = format(new Date(item.date), 'yyyy-MM-dd');
    const today = format(new Date(), 'yyyy-MM-dd');

    if (eventDate < today) return { label: 'Completed', className: 'is-completed' };
    if (eventDate > today) return { label: 'Upcoming', className: 'is-upcoming' };

    if (item.end_time) {
      const [hours, minutes] = item.end_time.split(':').map(Number);
      const eventEnd = new Date();
      eventEnd.setHours(hours, minutes || 0, 0, 0);
      if (eventEnd < new Date()) return { label: 'Completed', className: 'is-completed' };
    }

    return { label: 'Today', className: 'is-today' };
  };

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let frameId = 0;
    const updateDecorations = () => {
      frameId = 0;
      if (!itineraryPageRef.current) return;
      itineraryPageRef.current.style.setProperty('--itinerary-parallax-slow', `${window.scrollY * 0.18}px`);
      itineraryPageRef.current.style.setProperty('--itinerary-parallax-fast', `${window.scrollY * 0.32}px`);
    };
    const handleScroll = () => {
      if (!frameId) frameId = window.requestAnimationFrame(updateDecorations);
    };
    updateDecorations();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, []);

  return (
    <DashboardLayout>
      <div className="itinerary-page space-y-6" ref={itineraryPageRef}>
        <div className="itinerary-page__decor" aria-hidden="true">
          <span className="itinerary-page__pattern itinerary-page__pattern--curve-one" />
          <span className="itinerary-page__pattern itinerary-page__pattern--curve-two" />
          <span className="itinerary-page__pattern itinerary-page__pattern--curve-three" />
          <span className="itinerary-page__pattern itinerary-page__pattern--blush-one" />
          <span className="itinerary-page__pattern itinerary-page__pattern--blush-two" />
          <span className="itinerary-page__pattern itinerary-page__pattern--sparkles" />
        </div>
        {/* Header */}
        <StickyActionHeader className="flex-row items-center gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="font-serif text-2xl font-bold text-wedding-charcoal" data-testid="itinerary-title">
              Event Itinerary
            </h1>
            <p className="text-muted-foreground">{visibleItinerary.length} events scheduled</p>
          </div>
          {canManageItinerary && (
            <Dialog
              open={showAddDialog}
              onOpenChange={(open) => {
                setShowAddDialog(open);
                if (!open) {
                  setNewItem(createEmptyItineraryItem());
                }
              }}
            >
              <div className="flex w-auto flex-shrink-0 flex-nowrap items-center justify-end gap-2" data-header-action-group>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setConfirmItineraryMessage(true)}
                  disabled={sendingItineraryMessage || itinerary.length === 0}
                  aria-label="Send itinerary update"
                  title="Send itinerary update"
                  data-header-action
                  data-testid="send-itinerary-message-btn"
                >
                  <WhatsappLogo className="w-4 h-4 mr-2" />
                  {sendingItineraryMessage ? 'Sending...' : 'Send Update'}
                </Button>
                <DialogTrigger asChild>
                  <Button
                    className="bg-wedding-sage hover:bg-wedding-sage/90"
                    aria-label="Add itinerary event"
                    title="Add itinerary event"
                    data-header-action
                    data-testid="add-itinerary-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />Event
                  </Button>
                </DialogTrigger>
              </div>
              <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
                <>
                  <DialogHeader>
                    <DialogTitle>Add Itinerary Event</DialogTitle>
                    <DialogDescription>{eventTypeConfig.labels.itineraryAddDescription}</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleAddItem} className="space-y-3">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Event Name *</Label>
                      <Input
                        id="name"
                        value={newItem.name}
                        onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                        placeholder={eventTypeConfig.labels.itineraryNamePlaceholder}
                        required
                        data-testid="itinerary-name-input"
                      />
                    </div>
                    <div>
                      <EventAwareDatePicker
                        id="date"
                        label="Date"
                        value={newItem.date}
                        onChange={(date) => setNewItem({ ...newItem, date })}
                        eventStartDate={currentEvent?.start_date}
                        eventEndDate={currentEvent?.end_date}
                        required
                        data-testid="itinerary-date-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="start_time">Start Time *</Label>
                      <Input
                        id="start_time"
                        type="time"
                        value={newItem.start_time}
                        onChange={(e) => setNewItem({ ...newItem, start_time: e.target.value })}
                        required
                        data-testid="itinerary-start-time-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="end_time">End Time</Label>
                      <Input
                        id="end_time"
                        type="time"
                        value={newItem.end_time}
                        onChange={(e) => setNewItem({ ...newItem, end_time: e.target.value })}
                        data-testid="itinerary-end-time-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="dress_code">Dress Code</Label>
                      <Input
                        id="dress_code"
                        value={newItem.dress_code}
                        onChange={(e) => setNewItem({ ...newItem, dress_code: e.target.value })}
                        placeholder="e.g., Traditional"
                        data-testid="itinerary-dress-code-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={newItem.description}
                      onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                      rows={2}
                      data-testid="itinerary-description-input"
                    />
                  </div>
                  {eventTypeConfig.features.showGuestSides && <div>
                    <Label>Event side</Label>
                    <ToggleGroup
                      type="single"
                      variant="outline"
                      value={newItem.side || 'both'}
                      onValueChange={(side) => side && setNewItem({ ...newItem, side })}
                      className="mt-2 justify-start"
                      aria-label="Event side"
                    >
                      <ToggleGroupItem value="both" aria-label="Both sides">Both sides</ToggleGroupItem>
                      <ToggleGroupItem value="bride" aria-label="Bride side">Bride side</ToggleGroupItem>
                      <ToggleGroupItem value="groom" aria-label="Groom side">Groom side</ToggleGroupItem>
                    </ToggleGroup>
                  </div>}
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">Venue Details</h4>
                    <VenueSelector
                      value={newItem.venue}
                      onChange={(venue) => setNewItem({ ...newItem, venue })}
                      eventId={currentEvent?.id}
                      savedVenues={savedVenues}
                      showMap
                    />
                  </div>
                  {eventTypeConfig.features.showAccommodation && <div className="flex items-center justify-between gap-4 border-t pt-4">
                    <div>
                      <Label htmlFor="needs-stay">Guests need a hotel stay for this event?</Label>
                      <p className="text-xs text-muted-foreground">
                        {newItem.needs_stay
                          ? 'A hotel will be added from the venue details and can be reviewed later.'
                          : 'Turn this on when guests need accommodation.'}
                      </p>
                    </div>
                    <Switch
                      id="needs-stay"
                      checked={newItem.needs_stay}
                      onCheckedChange={(needs_stay) => setNewItem({ ...newItem, needs_stay })}
                      aria-label="Guests need a hotel stay"
                    />
                  </div>}
                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={addingItem}
                      className="bg-wedding-sage hover:bg-wedding-sage/90"
                      data-testid="save-itinerary-btn"
                    >
                      {addingItem ? 'Saving...' : 'Add Event'}
                    </Button>
                  </DialogFooter>
                  </form>
                </>
              </DialogContent>
            </Dialog>
          )}
        </StickyActionHeader>

        {/* Timeline */}
        <GuestItineraryRsvpProvider
          eventId={currentEvent?.id}
          enabled={isGuestInvitationView}
          onInvitationScopeLoaded={handleInvitationScopeLoaded}
        >
        <div className="itinerary-timeline">
          {Object.entries(groupedItinerary).sort().flatMap(([date, events]) =>
            events.map((item) => {
              const duration = getDuration(item.start_time, item.end_time);
              const status = getEventStatus(item);
              return (
              <article key={item.id} className="itinerary-timeline__row">
                <div className="itinerary-timeline__date" aria-label={format(new Date(date), 'EEEE, MMMM d, yyyy')}>
                  <strong>{format(new Date(date), 'd')}</strong>
                  <span>{format(new Date(date), 'MMM')}</span>
                  <span>{format(new Date(date), 'EEE')}</span>
                </div>
                <div className="itinerary-timeline__rail" aria-hidden="true">
                  <span />
                </div>
                <Card className="itinerary-event-card" data-testid={`itinerary-${item.id}`}>
                  <CardContent className="itinerary-event-card__content">
                    {canManageItinerary && (
                      <div className="itinerary-event-card__actions">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-wedding-charcoal"
                              aria-label={`Actions for ${item.name}`}
                              title="Event actions"
                            >
                              <DotsThree style={{ width: 24, height: 24 }} weight="bold" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-40">
                            <DropdownMenuLabel>Event actions</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onSelect={() => handleEditItem(item)}
                              data-testid={`edit-itinerary-${item.id}`}
                            >
                              <PencilSimple className="h-4 w-4" />
                              Edit event
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={() => {
                                setDeleteError('');
                                setItemToDelete(item);
                                loadStayData();
                              }}
                              className="text-destructive focus:text-destructive"
                              data-testid={`delete-itinerary-${item.id}`}
                            >
                              <Trash className="h-4 w-4" />
                              Delete event
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    )}
                    <div className="itinerary-event-card__topline">
                      <span className="itinerary-event-card__time">
                        <Clock weight="regular" aria-hidden="true" />
                        {formatItineraryTime(item.start_time)}
                        {item.end_time && ` – ${formatItineraryTime(item.end_time)}`}
                      </span>
                      {duration && <span>{duration}</span>}
                    </div>
                    <div className="itinerary-event-card__heading">
                      <h3>{item.name}</h3>
                      <span className={`itinerary-event-card__status ${status.className}`}>
                        {status.label}
                      </span>
                    </div>
                    <div className="itinerary-event-card__details">
                      <div className="itinerary-event-card__detail">
                        <MapPinLine weight="regular" />
                        <div>
                          {item.venue?.name ? (
                          getVenueLocationUrl(item.venue) ? (
                            <a
                              href={getVenueLocationUrl(item.venue)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="itinerary-event-card__detail-title"
                            >
                              {item.venue.name}{item.venue.hall_name ? `, ${item.venue.hall_name}` : ''}
                            </a>
                          ) : (
                            <span className="itinerary-event-card__detail-title">
                              {item.venue.name}{item.venue.hall_name ? `, ${item.venue.hall_name}` : ''}
                            </span>
                          )
                          ) : <span className="itinerary-event-card__detail-title">Venue to be announced</span>}
                          <span>{item.venue?.address || item.venue?.floor || 'Location details coming soon'}</span>
                        </div>
                      </div>
                      <div className="itinerary-event-card__detail">
                        <TShirt weight="regular" />
                        <div>
                          <span className="itinerary-event-card__detail-title">{item.dress_code || 'Come as you are'}</span>
                          <span>{eventTypeConfig.features.showGuestSides ? getAudienceLabel(item.side) : 'Event attire'}</span>
                        </div>
                      </div>
                    </div>
                    {eventTypeConfig.features.showAccommodation && (getLinkedHotel(item) || item.needs_stay) && (
                      <div className="itinerary-event-card__stay">
                        <Bed />
                        {getLinkedHotel(item) ? `Stay: ${getLinkedHotel(item).hotel_name}` : 'Stay arrangements pending'}
                      </div>
                    )}
                    {item.description && <p className="itinerary-event-card__description">{item.description}</p>}
                  </CardContent>
                </Card>
              </article>
              );
            })
          )}
        </div>

        {visibleItinerary.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <CalendarCheck className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {isGuestInvitationView
                  ? guestInvitationScopeLoaded
                    ? 'No itinerary events are assigned to your invitation.'
                    : 'Loading your itinerary...'
                  : 'No events scheduled yet. Add your first event to create the itinerary.'}
              </p>
            </CardContent>
          </Card>
        )}
        </GuestItineraryRsvpProvider>

        {/* Edit Itinerary Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Itinerary Event</DialogTitle>
              <DialogDescription>Update event details</DialogDescription>
            </DialogHeader>
            {editingItem && (
              <form onSubmit={handleUpdateItem} className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-name">Event Name *</Label>
                    <Input
                      id="edit-name"
                      value={editingItem.name}
                      onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <EventAwareDatePicker
                      id="edit-date"
                      label="Date"
                      value={editingItem.date}
                      onChange={(date) => setEditingItem({ ...editingItem, date })}
                      eventStartDate={currentEvent?.start_date}
                      eventEndDate={currentEvent?.end_date}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="edit-start_time">Start Time *</Label>
                    <Input
                      id="edit-start_time"
                      type="time"
                      value={editingItem.start_time}
                      onChange={(e) => setEditingItem({ ...editingItem, start_time: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-end_time">End Time</Label>
                    <Input
                      id="edit-end_time"
                      type="time"
                      value={editingItem.end_time || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, end_time: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-dress_code">Dress Code</Label>
                    <Input
                      id="edit-dress_code"
                      value={editingItem.dress_code || ''}
                      onChange={(e) => setEditingItem({ ...editingItem, dress_code: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={editingItem.description || ''}
                    onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                    rows={2}
                  />
                </div>
                {eventTypeConfig.features.showGuestSides && <div>
                  <Label>Event side</Label>
                  <ToggleGroup
                    type="single"
                    variant="outline"
                    value={editingItem.side || 'both'}
                    onValueChange={(side) => side && setEditingItem({ ...editingItem, side })}
                    className="mt-2 justify-start"
                    aria-label="Event side"
                  >
                    <ToggleGroupItem value="both" aria-label="Both sides">Both sides</ToggleGroupItem>
                    <ToggleGroupItem value="bride" aria-label="Bride side">Bride side</ToggleGroupItem>
                    <ToggleGroupItem value="groom" aria-label="Groom side">Groom side</ToggleGroupItem>
                  </ToggleGroup>
                </div>}
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">Venue Details</h4>
                  <VenueSelector
                    value={editingItem.venue}
                    onChange={(venue) => setEditingItem({ ...editingItem, venue })}
                    eventId={currentEvent?.id}
                    savedVenues={savedVenues}
                    showMap
                  />
                </div>
                {eventTypeConfig.features.showAccommodation && <div className="flex items-center justify-between gap-4 border-t pt-4">
                  <div>
                    <Label htmlFor="edit-needs-stay">Guests need a hotel stay for this event?</Label>
                    <p className="text-xs text-muted-foreground">Turning this off removes the event from its stay plan.</p>
                  </div>
                  <Switch
                    id="edit-needs-stay"
                    checked={Boolean(editingItem.needs_stay)}
                    onCheckedChange={(needs_stay) => setEditingItem({ ...editingItem, needs_stay })}
                    aria-label="Guests need a hotel stay"
                  />
                </div>}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
                  <Button type="submit" className="bg-wedding-sage hover:bg-wedding-sage/90">
                    Update Event
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        <AlertDialog
          open={Boolean(itemToDelete)}
          onOpenChange={(open) => {
            if (!open && !deletingItem) {
              setItemToDelete(null);
              setDeleteError('');
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {itemToDelete?.name || 'this itinerary event'}?</AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>This removes the event from manager and guest itineraries.</p>
                  {loadingStayData ? (
                    <div className="rounded-md border bg-muted/30 p-3 text-sm" role="status">
                      Checking linked hotel stays and room assignments...
                    </div>
                  ) : blockingDeleteStayPlan ? (
                    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert">
                      <p className="font-semibold">
                        Room assignments must be cleared first
                      </p>
                      <p className="mt-1">
                        This is the last itinerary event linked to{' '}
                        <strong>{blockingDeleteStayPlan.title || blockingDeleteStayPlan.hotel_name || 'this hotel stay'}</strong>,
                        {' '}where {blockingAssignedGuestCount} guest{blockingAssignedGuestCount === 1 ? ' is' : 's are'} assigned to rooms.
                      </p>
                      <p className="mt-1">
                        Open the stay below, unassign the guests, then return here to delete the event.
                      </p>
                    </div>
                  ) : (
                    <p>This action cannot be undone.</p>
                  )}
                  {deleteError && (
                    <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" role="alert">
                      <p>{deleteError}</p>
                    </div>
                  )}
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deletingItem}>Cancel</AlertDialogCancel>
              {blockingDeleteStayPlan ? (
                <Button asChild className="bg-wedding-sage hover:bg-wedding-sage/90">
                  <Link to={blockingRoomsPath}>Open Rooms for this stay</Link>
                </Button>
              ) : loadingStayData ? (
                <Button disabled>Checking assignments...</Button>
              ) : (
                <AlertDialogAction
                  onClick={(event) => {
                    event.preventDefault();
                    handleDeleteItem();
                  }}
                  disabled={deletingItem}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {deletingItem ? 'Deleting...' : 'Delete event'}
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <WhatsAppSendConfirmationDialog
          open={confirmItineraryMessage}
          onOpenChange={setConfirmItineraryMessage}
          onConfirm={() => {
            setConfirmItineraryMessage(false);
            sendItineraryUpdateMessage();
          }}
          title="Send itinerary update?"
          description="This will send the latest itinerary details to all eligible guests on WhatsApp."
          confirmLabel="Send itinerary update"
          templateName="itinerary_update_notifications"
          templateVariables={{
            guestName: 'Guest',
            hostNames: eventTypeConfig.isWedding
              ? [currentEvent?.bride_name, currentEvent?.groom_name].filter(Boolean).join(' & ') || currentEvent?.name
              : currentEvent?.name,
            managerName: user?.company_name || user?.name || 'Your Event Team',
          }}
          disabled={sendingItineraryMessage}
          testId="confirm-itinerary-whatsapp-send-btn"
        />

      </div>
    </DashboardLayout>
  );
}
